import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { HexColorInput, HexColorPicker } from "react-colorful";
import BaseTemplate from "../components/BaseTemplate";
import PersistentGrid from "../components/PersistentGrid/PersistentGrid";
import PersistentGridResetButton from "../components/PersistentGrid/PersistentGridResetButton";
import PersistentGridClearFiltersButton from "../components/PersistentGrid/PersistentGridClearFiltersButton";
import { PersistentGridProvider } from "../contexts/PersistentGridContext";
import InternalUserRoute from "../util/internal_user_route";
import { themeColours } from "../util/themeColours";
import { applyLiveThemeOverride, clearLiveThemeOverride } from "../util/applyLiveThemeOverride";
import { EDB_CUSTOM_withHeaderTooltips } from "../util/AgGridFunctions";

type ColourKey = keyof typeof themeColours;
type Colours = Record<ColourKey, string>;

type ThemeRuleRow = {
  key: ColourKey;
  component: string; // coarse section, e.g. "Navbar" (GROUPS[].title)
  item: string; // specific rule within that section, e.g. "Navbar background" (LABELS[key])
  hex: string;
  valid: boolean;
};

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const BARE_HEX_RE = /^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Colour pickers commonly copy just "RRGGBB", no "#" - rather than making everyone remember to
// type the "#" back in, treat a bare 3/6-digit hex as shorthand for "#RRGGBB" automatically.
function normalizeHex(value: string): string {
  const trimmed = value.trim();
  return BARE_HEX_RE.test(trimmed) ? `#${trimmed}` : value;
}

const LABELS: Record<ColourKey, string> = {
  appBackground: "Overall background",
  defaultText: "Default text colour",
  navbarBackground: "Navbar background",
  navbarBackgroundSecondary: "Navbar background (bottom group)",
  navbarIcon: "Navbar icon / text",
  actionBarBackground: "ActionBar background",
  actionBarBorder: "ActionBar border",
  actionBarButtonEnabled: "ActionBar button (enabled)",
  actionBarButtonDisabled: "ActionBar button (disabled)",
  agGridContainerBackground: "AG-Grid container background",
  agGridInternalBackground: "AG-Grid internal (cell) background",
  agGridHeaderBar: "AG-Grid header bar",
  agGridGroupingBar: "AG-Grid grouping bar",
  agGridFilterBar: "AG-Grid filter bar",
  preFilterBackground: "PreFilter background",
  preFilterBorder: "PreFilter border",
};

const GROUPS: { title: string; keys: ColourKey[] }[] = [
  { title: "Overall", keys: ["appBackground", "defaultText"] },
  { title: "Navbar", keys: ["navbarBackground", "navbarBackgroundSecondary", "navbarIcon"] },
  {
    title: "ActionBar",
    keys: ["actionBarBackground", "actionBarBorder", "actionBarButtonEnabled", "actionBarButtonDisabled"],
  },
  {
    title: "AG-Grid",
    keys: [
      "agGridContainerBackground",
      "agGridInternalBackground",
      "agGridHeaderBar",
      "agGridGroupingBar",
      "agGridFilterBar",
    ],
  },
  {
    title: "PreFilter",
    keys: ["preFilterBackground", "preFilterBorder"],
  },
];

function generateFileContent(colours: Colours): string {
  const lines: string[] = [
    "// ============================================================================",
    "// EASY-EDIT COLOUR SCHEME",
    "// Change any hex value below, save the file, refresh your browser. That's it.",
    "//",
    "// This is the ONLY file you need to edit to change the app's colour scheme. Every colour in the",
    "// app reads from here - including ag-grid (the data-table library), which normally uses a",
    "// completely different styling mechanism (CSS custom properties, not Chakra props) and would",
    "// otherwise need its own separate edit - see applyAgGridColours.ts, which copies these same",
    "// values into ag-grid's CSS variables at startup so you never have to touch two files to change",
    "// one colour.",
    "// ============================================================================",
    "export const themeColours = {",
  ];
  for (const group of GROUPS) {
    lines.push(`  // ${group.title}`);
    for (const key of group.keys) {
      lines.push(`  ${key}: "${colours[key]}",`);
    }
    lines.push("");
  }
  lines.pop(); // drop trailing blank line
  lines.push("};");
  return lines.join("\n") + "\n";
}

// One row's colour-wheel picker, in its own component so each row's Popover only re-renders
// itself while dragging around the wheel, not the whole grid.
function ColourPickerButton({
  value,
  valid,
  label,
  onChange,
}: {
  value: string;
  valid: boolean;
  label: string;
  onChange: (hex: string) => void;
}) {
  return (
    <Popover placement="right" isLazy>
      <PopoverTrigger>
        <Button
          aria-label={`Pick colour for ${label}`}
          w="28px"
          h="28px"
          minW="28px"
          p={0}
          borderRadius="md"
          border="1px solid"
          borderColor="gray.300"
          bg={valid ? value : "gray.100"}
          _hover={{ opacity: 0.85 }}
        />
      </PopoverTrigger>
      <PopoverContent w="216px">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverBody>
          <VStack spacing={2} align="stretch">
            <Text fontSize="xs" fontWeight="semibold">
              {label}
            </Text>
            <Box className="theme-editor-colour-wheel">
              <HexColorPicker color={valid ? value : "#000000"} onChange={onChange} style={{ width: "100%" }} />
            </Box>
            <HexColorInput
              color={valid ? value : ""}
              onChange={onChange}
              prefixed
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "4px 8px",
                border: "1px solid #CBD5E0",
                borderRadius: "6px",
                fontFamily: "monospace",
                fontSize: "12px",
              }}
            />
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

type RuleCellRendererParams = ICellRendererParams<ThemeRuleRow> & {
  setValue: (key: ColourKey, value: string) => void;
};

function SwatchCellRenderer({ data, setValue }: RuleCellRendererParams) {
  if (!data) return null;
  return (
    <ColourPickerButton
      value={data.hex}
      valid={data.valid}
      label={data.item}
      onChange={(hex) => setValue(data.key, hex)}
    />
  );
}

function HexCellRenderer({ data, setValue }: RuleCellRendererParams) {
  if (!data) return null;
  return (
    <Input
      size="sm"
      value={data.hex}
      onChange={(e) => setValue(data.key, e.target.value)}
      isInvalid={!data.valid}
      fontFamily="mono"
    />
  );
}

// Standalone (see other Reports/pages) - reachable at /theme_editor, not linked from the NavBar
// (an editing tool for whoever maintains the colour scheme, not a page every user needs).
export default function ThemeEditor() {
  const [colours, setColours] = useState<Colours>({ ...themeColours });
  const [copied, setCopied] = useState(false);
  const { isOpen: isInstructionsOpen, onOpen: onInstructionsOpen, onClose: onInstructionsClose } = useDisclosure();

  const invalidKeys = useMemo(
    () => (Object.keys(colours) as ColourKey[]).filter((k) => !HEX_RE.test(colours[k])),
    [colours],
  );

  const setValue = useCallback((key: ColourKey, value: string) => {
    setColours((c) => ({ ...c, [key]: normalizeHex(value) }));
    setCopied(false);
  }, []);

  const resetAll = () => {
    setColours({ ...themeColours });
    setCopied(false);
  };

  const fileContent = useMemo(() => generateFileContent(colours), [colours]);

  // Live preview across the WHOLE running app (navbar, action bar, ag-grid, this very page too -
  // not a separate mock-up), for as long as this page is open. See applyLiveThemeOverride.ts for
  // how - this is a browser-local DOM override, so it only ever affects this one tab/user, never
  // anyone else, and is never persisted. Reverts to the real themeColours.ts values on unmount
  // (i.e. as soon as you navigate away from this page).
  useEffect(() => {
    if (invalidKeys.length === 0) {
      applyLiveThemeOverride(colours);
    }
  }, [colours, invalidKeys.length]);

  useEffect(() => clearLiveThemeOverride, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fileContent);
      setCopied(true);
    } catch {
      // Clipboard API can be blocked (permissions/non-secure context) - Download below still
      // works even if this button doesn't.
      setCopied(false);
    }
  };

  // Saves to the browser's normal Downloads folder as a real .ts file - a browser can't write
  // directly into frontend/src/util/ (no filesystem access from a webpage), so the file still
  // has to be moved into place by hand, but "download then drag into place" is one less manual
  // step than "copy, open the real file, select-all, paste".
  const handleDownload = () => {
    const blob = new Blob([fileContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "themeColours.ts";
    a.click();
    URL.revokeObjectURL(url);
  };

  const rowData = useMemo<ThemeRuleRow[]>(
    () =>
      GROUPS.flatMap((group) =>
        group.keys.map((key) => ({
          key,
          component: group.title,
          item: LABELS[key],
          hex: colours[key],
          valid: HEX_RE.test(colours[key]),
        })),
      ),
    [colours],
  );

  const columnDefs = useMemo<ColDef<ThemeRuleRow>[]>(
    () =>
      EDB_CUSTOM_withHeaderTooltips([
        {
          field: "component",
          headerName: "Component",
          width: 150,
          minWidth: 120,
          sortable: true,
          resizable: true,
          enableRowGroup: true,
        },
        { field: "item", headerName: "Item", flex: 1, minWidth: 220, sortable: true, resizable: true },
        {
          colId: "swatch",
          headerName: "Swatch",
          width: 90,
          minWidth: 90,
          sortable: false,
          resizable: false,
          filter: false,
          floatingFilter: false,
          cellRenderer: SwatchCellRenderer,
          cellRendererParams: { setValue },
        },
        {
          field: "hex",
          headerName: "Hex",
          width: 160,
          minWidth: 160,
          sortable: false,
          resizable: true,
          cellRenderer: HexCellRenderer,
          cellRendererParams: { setValue },
        },
      ]),
    [setValue],
  );

  const defaultColDef = useMemo<ColDef<ThemeRuleRow>>(
    () => ({ resizable: true, filter: true, floatingFilter: true }),
    [],
  );

  return (
    <InternalUserRoute>
      <PersistentGridProvider gridKey="theme-editor-grid">
        <BaseTemplate fitToWindow={true}>
          <BaseTemplate.Header>
            <HStack>
              <PersistentGridResetButton />
              <PersistentGridClearFiltersButton />
              <Button size="sm" variant="outline" onClick={onInstructionsOpen}>
                Instructions
              </Button>
              <Button variant="actionBar" onClick={handleDownload} isDisabled={invalidKeys.length > 0}>
                Download themeColours.ts
              </Button>
              <Button size="sm" onClick={handleCopy} isDisabled={invalidKeys.length > 0}>
                {copied ? "Copied!" : "Copy to clipboard"}
              </Button>
              <Button size="sm" variant="outline" onClick={resetAll}>
                Reset to current file
              </Button>
              {invalidKeys.length > 0 && (
                <Text color="red.500" fontSize="sm">
                  Not valid hex: {invalidKeys.map((k) => LABELS[k]).join(", ")}
                </Text>
              )}
            </HStack>
          </BaseTemplate.Header>
          <BaseTemplate.Body>
            <Box w="100%" h="100%">
              <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                <PersistentGrid<ThemeRuleRow>
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  getRowId={(p) => p.data.key}
                  rowHeight={44}
                  rowGroupPanelShow="always"
                  animateRows
                />
              </div>
            </Box>
          </BaseTemplate.Body>
        </BaseTemplate>
      </PersistentGridProvider>

      <Modal isOpen={isInstructionsOpen} onClose={onInstructionsClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Instructions</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text fontSize="sm" color="gray.600">
              Edit values in the grid (type a hex, or click a swatch for a colour wheel) and
              the whole app — including this very page — repaints live, in real time, so
              you're always looking at the real result, not a mock-up. This only happens in
              your own browser tab and is never saved anywhere; navigate away from this page
              and it reverts to normal. To make a change permanent for everyone, use
              Download/Copy above to get the updated file and drop it in at{" "}
              <code>frontend/src/util/themeColours.ts</code>, or copy it and paste over that
              file's contents — that's the file that actually controls the app's colours
              long-term.
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </InternalUserRoute>
  );
}
